import React from "react";
import { useTranslation } from "react-i18next";
import GenericTable from "@surgia/ui/containers/table/generic-table";
import Badge from "@surgia/ui/components/badge";
import productIcon from "@surgia/ui/icons/catalog/product.svg";
import brandIcon from "@surgia/ui/icons/catalog/brand.svg";
import checkedIcon from "@surgia/ui/icons/checkbox/checked.svg";
import uncheckedIcon from "@surgia/ui/icons/checkbox/unchecked.svg";
import { TableColumn, SortState } from "@surgia/ui/containers/table/constants";
interface ICommonProductsProps {
  data: any;
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  isCurrentlyAssigned: (id: string) => boolean;
  selectedAssignedProducts: string[];
  toggledProductsApiTracker: string[];
  setSelectedAssignedProducts: (
    products: string[] | ((prev: string[]) => string[])
  ) => void;
  setToggledProductsApiTracker: (
    products: string[] | ((prev: string[]) => string[])
  ) => void;
  // Add sorting props
  sortState?: SortState;
  onSort?: (sortState: SortState) => void;
}

const CommonProductsTable: React.FC<ICommonProductsProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  selectedAssignedProducts,
  toggledProductsApiTracker,
  setSelectedAssignedProducts,
  setToggledProductsApiTracker,
  sortState,
  onSort,
}) => {
  const { t } = useTranslation();

  const isCurrentlyAssigned = (id: string, initialValue: boolean) => {
    if (selectedAssignedProducts.includes(id)) {
      return true;
    }
    if (toggledProductsApiTracker.includes(id)) {
      return !initialValue;
    }
    return initialValue;
  };

  // Business-specific column configuration
  const columns: TableColumn[] = [
    {
      key: "sku",
      translationKey: "shared.table.sku",
      sortable: true,
      sortKey: "id",
      sortType: "number",
      render: (value: {
        surgiaProductSKU: string;
        assigned: boolean;
        id: string;
      }) => (
        <div className="flex items-center gap-2">
          <img
            src={
              isCurrentlyAssigned(value?.id, value?.assigned)
                ? checkedIcon
                : uncheckedIcon
            }
            alt="checked"
            onClick={() => {
              // Toggle the selection
              if (isCurrentlyAssigned(value?.id, value?.assigned)) {
                if (selectedAssignedProducts.includes(value?.id)) {
                  const newSelectedAssignedProducts =
                    selectedAssignedProducts.filter(
                      (id: string) => id !== value?.id
                    );
                  setSelectedAssignedProducts(newSelectedAssignedProducts);
                }
              } else if (
                !isCurrentlyAssigned(value?.id, value?.assigned) &&
                !value?.assigned
              ) {
                const newSelectedAssignedProducts = [
                  ...selectedAssignedProducts.filter((id) => id !== value?.id), // Remove if exists
                  value?.id,
                ];
                setSelectedAssignedProducts(newSelectedAssignedProducts);
              }
              if (
                isCurrentlyAssigned(value?.id, value?.assigned) ==
                value?.assigned
              ) {
                const newToggledProductsApiTracker = [
                  ...toggledProductsApiTracker,
                  value?.id,
                ];
                setToggledProductsApiTracker(newToggledProductsApiTracker);
              }
              if (
                isCurrentlyAssigned(value?.id, value?.assigned) !=
                value?.assigned
              ) {
                const newToggledProductsApiTracker =
                  toggledProductsApiTracker.filter(
                    (id: string) => id !== value?.id
                  );
                setToggledProductsApiTracker(newToggledProductsApiTracker);
              }
            }}
            className="cursor-pointer"
          />
          <div className="text-[#181D27] text-[14px] font-medium ">
            {value.surgiaProductSKU}
          </div>
        </div>
      ),
    },
    {
      key: "name",
      translationKey: "shared.table.product",
      sortable: false,
      render: (value: {
        surgiaProductName: string;
        surgiaProductImage: string;
      }) => (
        <div className=" text-gray-500 flex items-center gap-2">
          <img
            src={value.surgiaProductImage ?? productIcon}
            alt="surgiaProductImage"
            className="rounded-full w-[40px] h-[40px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.1)]"
          />{" "}
          <p className="table-row-main max-w-[150px]">
            {value.surgiaProductName}
          </p>
        </div>
      ),
    },
    {
      key: "variantsCount",
      translationKey: "Variation Group",
      sortable: false,
      sortType: "number",
      render: (value) => (
        <Badge variant="VARIATION" size="sm">
          {value}
        </Badge>
      ),
    },
    {
      key: "category",
      translationKey: "shared.table.category",
      sortable: false,
      align: "left",
      render: (value: {
        surgiaFirstCategory: string;
        surgiaSecondCategory: string;
        surgiaThirdCategory: string;
      }) => (
        <div className="flex items-center gap-2">
          <Badge variant="firstCategory" size="sm">
            {value.surgiaFirstCategory}
          </Badge>
          {value.surgiaSecondCategory && (
            <Badge variant="secondCategory" size="sm">
              {value.surgiaSecondCategory}
            </Badge>
          )}
          {value.surgiaThirdCategory && (
            <Badge variant="thirdCategory" size="sm">
              {value.surgiaThirdCategory}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "brand",
      translationKey: "shared.table.brand",
      sortable: false,
      render: (value: {
        surgiaBrandName: string;
        surgiaBrandImage: string;
      }) => (
        <div className=" flex items-center gap-[12px]">
          <img src={brandIcon} alt={`${value.surgiaBrandName}-image`} />
          <p className="table-row-main max-w-[100px]">
            {value.surgiaBrandName}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      translationKey: "shared.table.productStatus",
      sortable: true,
      align: "left",
      sortKey: "customerView",
      render: (value: boolean) => (
        <Badge variant={value ? "PUBLISHED" : "percentage"} rounded="full">
          {value ? t("shared.table.active") : t("shared.table.inactive")}
        </Badge>
      ),
    },
  ];

  return (
    <GenericTable
      columns={columns}
      data={data}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortState={sortState}
      onSort={onSort}
    />
  );
};

export default CommonProductsTable;
